import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  MapPin,
  Clock,
  DollarSign,
  Calendar,
  CheckCircle,
  Upload,
  ArrowLeft,
  Mail,
  Phone,
  FileText,
  Briefcase,
} from "lucide-react";
import { jobAPI } from "../services/api";

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState("");

  // Application form state
  const [formData, setFormData] = useState({
    applicant_name: "",
    applicant_email: "",
    phone: "",
    cover_letter: "",
    resume: null,
  });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        // const response = await jobAPI.getJobById(id);
        // if (response.error) {
        //   setError(response.error);
        // } else {
        //   setJob(response.data || response);
        // }
        let { job } = location?.state;
        setJob(job ? job : null);
      } catch (err) {
        setError("Failed to fetch job details");
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      resume: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmissionMessage("");

    if (
      !formData.applicant_name ||
      !formData.applicant_email ||
      !formData.phone
    ) {
      setFormError("Please fill out all required fields");
      return;
    }
    if (!formData.resume) {
      setFormError("Please upload your resume.");
      // Scroll to resume field for better UX
      setTimeout(() => {
        const resumeField = document.getElementById("resume-upload-label");
        if (resumeField)
          resumeField.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }

    try {
      const applicationData = new FormData();
      applicationData.append("applicant_name", formData.applicant_name);
      applicationData.append("email", formData.applicant_email);
      applicationData.append("phone", formData.phone);
      applicationData.append("cover_letter", formData.cover_letter);
      applicationData.append("cv_file", formData.resume);

      // applicationData.append('job_id', id);
      // applicationData.append('job_title', job.title);

      const res = await fetch(
        "https://techmindinfosys.com/server/api/cv-documents.php",
        {
          method: "POST",
          body: applicationData,
        }
      );
      if (!res.ok) throw new Error("Upload failed");

      alert("CV uploaded successfully!");
      // ✅ Reset form fields
      // form.reset();
      // if (formData.resume) {
      //   applicationData.append("resume", formData.resume);
      // }

      // const response = await jobAPI.applyForJob(id, applicationData);
      // const response={
      //   status:200,
      //   success:true
      // }
      if (res.status === 201 || res.success) {
        setSubmissionMessage(
          "Your application has been submitted successfully! We'll be in touch soon."
        );
        setApplicationSubmitted(true);
        setFormData({
          applicant_name: "",
          applicant_email: "",
          phone: "",
          cover_letter: "",
          resume: null,
        });
        setTimeout(() => {
          setApplicationSubmitted(false);
        }, 5000);
      } else {
        setFormError(
          res.message || "Submission failed. Please try again."
        );
      }
    } catch (err) {
      setFormError(
        "An error occurred while submitting your application. Please try again later."
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/careers")}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Careers
          </button>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Job Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The job listing you're looking for doesn't exist or has been
            removed.
          </p>
          <button
            onClick={() => navigate("/careers")}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            View All Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
              {job.title}
            </h1>
            {job.flag && (
              <img
                src={job.flag}
                alt="flag"
                className="w-7 h-5 rounded-sm border border-gray-200"
              />
            )}
          </div>
          <button
            className="px-6 py-2 rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all text-base mt-4 md:mt-0"
            onClick={() =>
              document
                .getElementById("application-form")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            Apply Now
          </button>
        </div>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-4 text-gray-600 text-base">
              <a href="#" className="text-blue-600 hover:underline font-medium">
                {job.company}
              </a>
              <span className="mx-1">·</span>
              <MapPin className="w-4 h-4 inline-block mr-1" />
              <span>{job.location}</span>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Job Description
              </h2>
              <p className="text-gray-700 mb-4 whitespace-pre-line">
                {job.description ||
                  `We are seeking a talented ${job.title} to join our team at ${job.company}. This is an exciting opportunity to work on cutting-edge projects in a collaborative environment.`}
              </p>
              {/* Responsibilities Section */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Responsibilities:
                </h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  {(job.responsibilities && job.responsibilities.length > 0
                    ? job.responsibilities
                    : [
                        "Design, develop, and maintain high-quality software solutions",
                        "Collaborate with cross-functional teams to define and implement new features",
                        "Participate in code reviews and contribute to technical documentation",
                        "Troubleshoot and resolve complex technical issues",
                        "Stay up-to-date with industry trends and best practices",
                      ]
                  ).map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
              {/* Requirements Section */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Requirements:
                </h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  {(job.requirements && job.requirements.length > 0
                    ? job.requirements
                    : [
                        (job.experienceLevel === "entry"
                          ? "Entry level"
                          : job.experienceLevel || "Proven") +
                          (job.skills && job.skills.length > 0
                            ? ` experience in ${job.skills.join(", ")}`
                            : " experience in relevant field"),
                        "Strong problem-solving skills and attention to detail",
                        "Excellent communication and teamwork abilities",
                        "Bachelor's degree in Computer Science or related field (or equivalent experience)",
                        "Ability to work in a fast-paced, collaborative environment",
                      ]
                  ).map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
              {/* Benefits Section */}
              <div className="mb-2">
                <h3 className="font-semibold text-gray-800 mb-2">Benefits:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  {(job.benefits && job.benefits.length > 0
                    ? job.benefits
                    : [
                        "Competitive salary and benefits package",
                        "Flexible working arrangements",
                        "Professional development opportunities",
                        "Collaborative and innovative work environment",
                        "Regular team events and activities",
                      ]
                  ).map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            {/* Application Form */}
            <div
              id="application-form"
              className="bg-white rounded-xl border border-gray-100 p-6 mb-8"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Apply for this Position
              </h2>
              {formError && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
                  <p className="text-sm font-medium text-red-800">
                    {formError}
                  </p>
                </div>
              )}
              {applicationSubmitted ? (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-6">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <p className="text-sm font-medium text-green-800">
                      {submissionMessage}
                    </p>
                  </div>
                </div>
              ) : null}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="applicant_name"
                      value={formData.applicant_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="applicant_email"
                      value={formData.applicant_email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="+1 (234) 567-8910"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin || ""}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Resume/CV <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-md p-6 text-center ${
                      formError === "Please upload your resume."
                        ? "border-red-500 bg-red-50"
                        : "border-gray-300"
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      id="resume-upload"
                      required
                    />
                    <label
                      htmlFor="resume-upload"
                      id="resume-upload-label"
                      className="cursor-pointer block"
                    >
                      <div className="text-gray-500 mb-2">
                        {formData.resume
                          ? formData.resume.name
                          : "Upload a file or drag and drop"}
                      </div>
                      <span className="inline-block px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors">
                        {formData.resume ? "Change file" : "Select file"}
                      </span>
                    </label>
                    <div className="text-xs text-gray-500 mt-2">
                      PDF, DOC, DOCX up to 10MB
                    </div>
                    {formError === "Please upload your resume." && (
                      <div className="text-red-600 text-sm mt-2">
                        {formError}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Cover Letter
                  </label>
                  <textarea
                    name="cover_letter"
                    value={formData.cover_letter}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  />
                </div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    required
                    className="mr-2"
                    id="privacy"
                  />
                  <label htmlFor="privacy" className="text-gray-600 text-sm">
                    I agree to the{" "}
                    <a href="#" className="text-blue-600 underline">
                      privacy policy
                    </a>{" "}
                    and consent to having my data processed for this job
                    application.
                  </label>
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-semibold text-base"
                >
                  Submit Application
                </button>
              </form>
            </div>
          </div>
          {/* Summary Card */}
          <aside className="w-full lg:w-[340px] flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-6">
              <h3 className="font-bold text-lg text-gray-900 mb-4">
                Job Summary
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-gray-600 text-sm">
                  <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                  <span>Posted</span>
                  <span className="ml-auto text-gray-900 font-medium">
                    {job.posted || "Recently"}
                  </span>
                </li>
                <li className="flex items-center text-gray-600 text-sm">
                  <DollarSign className="w-4 h-4 mr-2 text-blue-600" />
                  <span>Salary</span>
                  <span className="ml-auto text-gray-900 font-medium">
                    {job.salary}
                  </span>
                </li>
                <li className="flex items-center text-gray-600 text-sm">
                  <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
                  <span>Employment Type</span>
                  <span className="ml-auto text-gray-900 font-medium">
                    {job.type}
                  </span>
                </li>
                <li className="flex items-center text-gray-600 text-sm">
                  <Clock className="w-4 h-4 mr-2 text-blue-600" />
                  <span>Experience Level</span>
                  <span className="ml-auto text-gray-900 font-medium">
                    {job.experienceLevel || "-"}
                  </span>
                </li>
              </ul>
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                  Required Skills
                </h4>
                <div className="flex flex-wrap gap-2">
                  {job.skills && job.skills.length > 0 ? (
                    job.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {skill}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Link to="/contact">
                  <button className="w-full border border-blue-600 text-blue-600 font-semibold rounded-md py-2 hover:bg-blue-50 transition-colors">
                    Contact Recruiter
                  </button>
                </Link>
              </div>
            </div>
            {/* Share section (optional) */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                Share This Job
              </h4>
              <div className="flex gap-3">
                <a href="#" className="text-gray-400 hover:text-blue-600">
                  <svg width="20" height="20" fill="currentColor">
                    <path d="M18 2.01 6 2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4.01a2 2 0 0 0-2-2zm0 14H6V4h12v12z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-blue-600">
                  <svg width="20" height="20" fill="currentColor">
                    <path d="M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-blue-600">
                  <svg width="20" height="20" fill="currentColor">
                    <path d="M17.707 6.293a1 1 0 0 0-1.414 0L10 12.586 7.707 10.293a1 1 0 0 0-1.414 1.414l3 3a1 1 0 0 0 1.414 0l7-7a1 1 0 0 0 0-1.414z" />
                  </svg>
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default JobDetailPage;
